import { useState } from 'react';
import { gleichmaessig, ausbalanciert, bereiche, MAX_UNTERFAECHER } from '../services/regalUnterfach';

/**
 * 🔤 Unterfächer je Buchstabe einstellen (Setup, Schritt Sortierung).
 * value = { B: ['f','k','p','u'], … } (Anfangsbuchstaben der Unterfächer 2…n), onChange(neu).
 * stats = Pakete je Buchstabe/zweitem Buchstaben aus der eigenen Paketliste (zaehleNachnamen) —
 * damit lässt sich „nach Paketmenge" aufteilen und sehen, wie voll jedes Unterfach würde.
 */
const ABC = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const MIN_PAKETE = 20; // darunter ist „nach Paketmenge" Zufall

export default function SubShelfEditor({ value, onChange, stats, t, colors }) {
  const dark = colors?.darkMode;
  const farbe = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', flaeche: dark ? '#1b1f27' : '#f6f7f9' };
  const groesster = ABC.split('').reduce((a, b) => ((stats?.[b]?.gesamt || 0) > (stats?.[a]?.gesamt || 0) ? b : a), 'B');
  const [buchstabe, setBuchstabe] = useState(groesster);
  const [modus, setModus] = useState({}); // je Buchstabe: 'alphabet' | 'menge'

  const st = stats?.[buchstabe] || { gesamt: 0, zweite: {} };
  const genug = st.gesamt >= MIN_PAKETE;
  const grenzen = value?.[buchstabe] || [];
  const n = grenzen.length + 1;
  // Gewählte Aufteilung beim erneuten Öffnen aus den gespeicherten Grenzen ablesen — sonst stand nach dem Speichern
  // immer „nach Paketmenge" markiert, obwohl „nach Alphabet" gewählt war (Folkert, 08.10.2026: „doesn't save the setting")
  const gespeichertArt = grenzen.length ? (grenzen.join(',') === gleichmaessig(n).join(',') ? 'alphabet' : 'menge') : null;
  const art = modus[buchstabe] || gespeichertArt || (genug ? 'menge' : 'alphabet');

  const setze = (anzahl, wie) => {
    const k = Math.max(1, Math.min(MAX_UNTERFAECHER, anzahl));
    const neu = { ...(value || {}) };
    if (k === 1) delete neu[buchstabe];
    else neu[buchstabe] = wie === 'menge' && genug ? ausbalanciert(st.zweite, k) : gleichmaessig(k);
    setModus((m) => ({ ...m, [buchstabe]: wie }));
    onChange(neu);
  };
  const paketeIn = (b) => {
    let s = 0;
    for (const [z, v] of Object.entries(st.zweite)) if (z >= b.von && z <= b.bis) s += v;
    return s;
  };

  const knopf = (an) => ({ padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    border: `2px solid ${an ? '#1976d2' : farbe.rand}`, background: an ? '#1976d2' : 'transparent', color: an ? '#fff' : farbe.text });

  return (
    <div data-testid="sub-editor" style={{ marginTop: 14, padding: 12, borderRadius: 10, border: `1px solid ${farbe.rand}`, color: farbe.text }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>🔤 {t('setup.sub.title')}</strong>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: dark ? '#1f3a2e' : '#e8f5e9', color: dark ? '#81c784' : '#2e7d32', border: `1px solid ${dark ? '#2e7d32' : '#a5d6a7'}` }}>
          OPTIONAL
        </span>
      </div>
      <div style={{ marginTop: 6, padding: '8px 10px', borderRadius: 8, background: dark ? 'rgba(46,125,50,0.15)' : '#f1f8e9', border: `1px solid ${dark ? '#2e7d32' : '#c5e1a5'}`, fontSize: 12, color: dark ? '#c8e6c9' : '#1b5e20', lineHeight: 1.45 }}>
        {Object.keys(value || {}).length === 0
          ? '✅ Fertig voreingestellt: 1 Fach pro Buchstabe (A–Z). Du musst hier nichts anlegen! Nur falls ein Buchstabe im Regal zu voll wird, kannst du ihn hier optional in Unterfächer (z. B. B1, B2) teilen.'
          : `✅ Unterfächer aktiv (${Object.entries(value || {}).map(([k, v]) => `${k}×${(v?.length || 0) + 1}`).join(', ')}) — alle übrigen Buchstaben haben automatisch 1 Fach.`}
      </div>
      <div style={{ fontSize: 13, color: farbe.sub, marginTop: 4, lineHeight: 1.45 }}>{t('setup.sub.intro')}</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 4, marginTop: 10 }}>
        {ABC.split('').map((b) => {
          const k = (value?.[b]?.length || 0) + 1;
          const gewaehlt = b === buchstabe;
          return (
            <button key={b} type="button" data-testid={`sub-letter-${b}`} data-count={k} aria-pressed={gewaehlt} onClick={() => setBuchstabe(b)}
              style={{ padding: '6px 0', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 700, lineHeight: 1.1,
                border: `2px solid ${gewaehlt ? '#1976d2' : (k > 1 ? '#4caf50' : farbe.rand)}`,
                background: gewaehlt ? (dark ? '#1c2b3f' : '#e8f1fc') : 'transparent', color: farbe.text }}>
              {b}
              <div style={{ fontSize: 10, fontWeight: 600, color: k > 1 ? '#2e7d32' : farbe.sub }}>{k > 1 ? `×${k}` : '·'}</div>
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: farbe.flaeche }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <strong>{t('setup.sub.count', { letter: buchstabe })}</strong>
            {stats && <div data-testid="sub-parcels" style={{ fontSize: 12, color: farbe.sub }}>{t('setup.sub.parcels', { n: st.gesamt })}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button type="button" data-testid="sub-minus" disabled={n <= 1} onClick={() => setze(n - 1, art)} style={{ ...knopf(false), width: 40, fontSize: 20, padding: '4px 0', opacity: n <= 1 ? 0.4 : 1 }}>−</button>
            <strong data-testid="sub-count" style={{ fontSize: 20, minWidth: 22, textAlign: 'center' }}>{n}</strong>
            <button type="button" data-testid="sub-plus" disabled={n >= MAX_UNTERFAECHER} onClick={() => setze(n + 1, art)} style={{ ...knopf(false), width: 40, fontSize: 20, padding: '4px 0', opacity: n >= MAX_UNTERFAECHER ? 0.4 : 1 }}>+</button>
          </div>
        </div>

        {n > 1 && (
          <>
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              <button type="button" data-testid="sub-mode-alphabet" aria-pressed={art === 'alphabet' || !genug} style={knopf(art === 'alphabet' || !genug)} onClick={() => setze(n, 'alphabet')}>{t('setup.sub.byAlphabet')}</button>
              <button type="button" data-testid="sub-mode-menge" aria-pressed={art === 'menge' && genug} disabled={!genug} style={{ ...knopf(art === 'menge' && genug), opacity: genug ? 1 : 0.45 }} onClick={() => setze(n, 'menge')}>{t('setup.sub.byVolume')}</button>
            </div>
            {!genug && <div style={{ fontSize: 12, color: farbe.sub, marginTop: 6, lineHeight: 1.4 }}>{t('setup.sub.tooFew', { min: MIN_PAKETE })}</div>}
            {/* Info: was die gewählte Aufteilung im Alltag bedeutet (Wunsch 07.10.2026: Folge jeder Wahl erklären) */}
            <div data-testid="sub-info" style={{ display: 'flex', gap: 8, marginTop: 8, padding: '8px 10px', borderRadius: 8, border: `1px solid ${farbe.rand}`, fontSize: 12, color: farbe.sub, lineHeight: 1.45 }}>
              <span aria-hidden="true" style={{ fontSize: 14 }}>ℹ️</span>
              <div>
                <div>{t(art === 'menge' && genug ? 'setup.sub.volumeInfo' : 'setup.sub.alphabetInfo')}</div>
                <div style={{ marginTop: 4 }}>{t('setup.sub.newOnly')}</div>
              </div>
            </div>
            <div data-testid="sub-ranges" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {bereiche(grenzen).map((b) => (
                <span key={b.nr} style={{ padding: '5px 9px', borderRadius: 6, border: `1px solid ${farbe.rand}`, fontSize: 13, background: dark ? '#23272f' : '#fff' }}>
                  <strong>{buchstabe}{b.nr}</strong> {b.von === b.bis ? b.von.toUpperCase() : `${b.von.toUpperCase()}–${b.bis.toUpperCase()}`}
                  {st.gesamt > 0 && <span style={{ color: farbe.sub }}> · {paketeIn(b)}</span>}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
